import { useEffect, useState, useCallback } from "react";
import { CalendarClock, MessageCircle, Download, RefreshCw } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const RANGOS = [
  { v: "0", l: "Hoy" },
  { v: "1", l: "Hoy y mañana" },
  { v: "7", l: "Próximos 7 días" },
];

// Normaliza el teléfono del cliente a formato E.164 sin "+" para wa.me.
// 10 dígitos → antepone "52"; 12 dígitos que ya empiezan con "52", o 13 que empiezan con
// "521", se usan tal cual; cualquier otro caso se considera inválido (botón deshabilitado).
function telefonoWhatsApp(telRaw) {
  const digitos = String(telRaw || "").replace(/\D/g, "");
  if (digitos.length === 10) return `52${digitos}`;
  if (digitos.length === 12 && digitos.startsWith("52")) return digitos;
  if (digitos.length === 13 && digitos.startsWith("521")) return digitos;
  return null;
}

function fechaDDMMAAAA(f) {
  const m = String(f || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return f || "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function mensajeRecordatorio(item, entityNombre) {
  const primerNombre = String(item.cliente || "").trim().split(/\s+/)[0] || "cliente";
  const cuando = item.es_hoy ? "hoy" : `el ${fechaDDMMAAAA(item.fecha_pago)}`;
  let msg = `Hola ${primerNombre}, le recordamos que su pago de ${mxn(item.monto)} de su crédito ` +
    `${item.numero_credito} vence ${cuando}. Evite recargos realizando su pago a tiempo. Gracias.`;
  if (entityNombre) msg += ` ${entityNombre}`;
  return msg;
}

function descargarCSV(rows) {
  const headers = ["Fecha", "Cliente", "No. crédito", "Cuota", "Monto", "Asesora", "Teléfono"];
  const lineas = [headers.join(",")].concat(
    rows.map((r) => [
      fdate(r.fecha_pago), r.cliente, r.numero_credito, r.cuota_no, r.monto, r.asesora, r.telefono,
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
  );
  const blob = new Blob(["\uFEFF" + lineas.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `proximos_cobros_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ProximosCobrosTab() {
  const { user } = useAuth();
  const [rango, setRango] = useState("1");
  const [asesora, setAsesora] = useState("todas");
  const [data, setData] = useState({ items: [], total: 0, monto_total: 0 });
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/cartera/proximos-cobros", { params: { dias: Number(rango) } });
      setData(d);
    } catch {
      toast.error("No se pudieron cargar los próximos cobros");
    } finally {
      setLoading(false);
    }
  }, [rango]);
  useEffect(() => { cargar(); }, [cargar]);

  const asesoras = Array.from(new Set((data.items || []).map((i) => i.asesora).filter(Boolean))).sort();
  const filtrados = asesora === "todas" ? (data.items || [])
    : (data.items || []).filter((i) => i.asesora === asesora);
  const montoFiltrado = filtrados.reduce((acc, i) => acc + Number(i.monto || 0), 0);

  const recordar = (item) => {
    const tel = telefonoWhatsApp(item.telefono);
    if (!tel) return;
    const texto = mensajeRecordatorio(item, user?.entity_nombre);
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(texto)}`, "_blank", "noopener,noreferrer");
  };

  const COLS = [
    { key: "fecha_pago", label: "Fecha", render: (r) => (
      <span className="flex items-center gap-1.5">
        {fdate(r.fecha_pago)}
        {r.es_hoy && (
          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">HOY</span>
        )}
      </span>
    ) },
    { key: "cliente", label: "Cliente" },
    { key: "numero_credito", label: "No. crédito" },
    { key: "cuota_no", label: "Cuota" },
    { key: "monto", label: "Monto", kind: "currency" },
    { key: "asesora", label: "Asesora" },
    { key: "telefono", label: "Teléfono", render: (r) => r.telefono || <span className="text-slate-400">—</span> },
    { key: "acc", label: "", render: (r) => {
      const tel = telefonoWhatsApp(r.telefono);
      return (
        <Button
          size="sm"
          variant="outline"
          disabled={!tel}
          title={tel ? undefined : "Sin teléfono válido"}
          data-testid={`proximos-wa-${r.credito_id}-${r.cuota_no}`}
          onClick={(e) => { e.stopPropagation(); recordar(r); }}
        >
          <MessageCircle className="mr-1 h-3.5 w-3.5" /> Recordar por WhatsApp
        </Button>
      );
    } },
  ];

  return (
    <div className="space-y-4 py-4" data-testid="proximos-cobros">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="kpi-proximos-cuotas" label="Cuotas por cobrar" value={filtrados.length}
          icon="CalendarClock" accent="amber" />
        <StatCard testid="kpi-proximos-monto" label="Monto por cobrar" value={mxn(montoFiltrado)}
          icon="Wallet" accent="primary" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div>
          <Label className="mb-1 block text-xs text-slate-500">Rango</Label>
          <Select value={rango} onValueChange={setRango}>
            <SelectTrigger className="w-48" data-testid="proximos-rango"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGOS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs text-slate-500">Asesora</Label>
          <Select value={asesora} onValueChange={setAsesora}>
            <SelectTrigger className="w-56" data-testid="proximos-asesora"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              {asesoras.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" className="mt-5" onClick={cargar} disabled={loading}
          data-testid="proximos-actualizar">
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
        <Button variant="outline" size="sm" className="mt-5 ml-auto" disabled={filtrados.length === 0}
          onClick={() => descargarCSV(filtrados)} data-testid="proximos-descargar">
          <Download className="mr-1.5 h-4 w-4" /> Descargar
        </Button>
      </div>

      <DataTable testid="table-proximos-cobros" columns={COLS} rows={filtrados} loading={loading}
        emptyLabel="No hay cuotas por cobrar en este rango." />
    </div>
  );
}
